/*
 *   File : order-list.js
 *   Author : https://evoqins.com
 *   Description : Customer order list
 *   Version : 1.0.0
*/


import { Fragment, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Custom components
import { DataTableContainer } from "../../../Components/DataTable";
import { CustomSelect, SearchInput } from "../../../Components/FormElements";
import { Icon } from "../../../Components/Icon";
import { Badge } from "../../../Components/Badge";
import { CustomCalendar, EmptyScreen } from "../../../Components/Others";
import { PrimaryButton, SecondaryButton } from "../../../Components/Buttons";

// Custom styles
import Colors from '../../../Styles/color.module.scss';

// Services
import APIService from "../../../Services/api-service";
import { _formatDate } from "../../../Helper/helper";
import Config from "../../../Helper/config";

// Table custom style
const TABLE_STYLE = {
    table: {
        style: {
            borderRadius: "0px",
        },
    },

    headRow: {
        style: {
            borderBottomColor: 'transparent',
            paddingLeft: "32px",
            color: Colors.dark_gray,
            fontFamily: "Montserrat-Bold",
            fontSize: "14px",
            lineHeight: "18px",
            paddingTop: "0px",
            paddingBottom: '0px',
            minHeight: "32px"
        }
    },
    rows: {
        style: {
            paddingLeft: "32px",
            borderBottom: 'none !important',
            cursor: 'pointer',
            '&:nth-child(odd)': {
                backgroundColor: '#0D0D0D05',

            },
            '&:hover': {
                backgroundColor: Colors.lavender_mist
            },

        },
    },
    cells: {
        style: {
            paddingTop: '16px',
            paddingBottom: '16px',
            color: Colors.dark_charcoal,
            fontFamily: "Montserrat-Medium",
            fontSize: "14px",
            lineHeight: "20px",
            paddingLeft: "0px",
            userSelect: "none",
        }
    },
    headCells: {
        style: {
            paddingLeft: "0px"
        }
    },
    pagination: {
        style: {
            paddingRight: "50px",
            paddingTop: "10px",
            borderTop: "none"
        }
    }
};

const ORDER_STATUS_OPTION = [
    {
        label: "All",
        value: null
    },
    {
        label: "Success",
        value: "SUCCESSFUL"
    },
    {
        label: "Failed",
        value: "FAILED"
    },
    {
        label: "Processing",
        value: "PROCESSING"
    },
    {
        label: "Partially Success",
        value: "PARTIALLY SUCCESSFUL"
    },
]

const PAYMENT_STATUS_OPTION = [
    {
        label: "All",
        value: null
    },
    {
        label: "Success",
        value: "SUCCESS"
    },
    {
        label: "Failed",
        value: "FAILED"
    },
    {
        label: "Processing",
        value: "PROCESSING"
    },
]

const GOAL_TYPE = [
    {
        label: "Goal level",
        value: true
    },
    {
        label: "Fund level",
        value: false
    },
]

const PAYMENT_METHODS = [
    {
        label: "All",
        value: null
    },
    {
        label: "NETBANKING",
        value: 1
    },
    {
        label: "UPI",
        value: 2
    }
]

const ORDER_TYPES = [
    {
        label: "All",
        value: null
    },
    {
        label: "Lumpsum",
        value: 1
    },
    {
        label: "SIP",
        value: 2
    },
    {
        label: "Withdrawal",
        value: 3
    }
]
const OrdersList = () => {

    const disabledDays = { after: new Date() };
    const location = useLocation();
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [filterEnabled, setFilterEnabled] = useState(true);
    const [selectedOrderType, setSelectedOrderType] = useState(ORDER_TYPES[0]);
    const [selectedPaymentStatus, setSelectedPaymentStatus] = useState(PAYMENT_STATUS_OPTION[0]);
    const [selectedOrderStatus, setSelectedOrderStatus] = useState(ORDER_STATUS_OPTION[0]);
    const [selectedMilestone, setSelectedMilestone] = useState(GOAL_TYPE[0]);
    const [selectedPaymentType, setSelectedPaymentType] = useState(PAYMENT_METHODS[0]);
    const [fromDate, setFromDate] = useState("DD-MM-YYYY");
    const [endDate, setEndDate] = useState("DD-MM-YYYY");
    const [showTooltip, setShowTooltip] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState(null);
    const [tooltipContent, setTooltipContent] = useState("");
    const [pageNumber, setPageNumber] = useState(1);
    const [orderList, setOrderList] = useState([]);
    const [screenLoader, setScreenLoader] = useState(true);
    const [filter, setFilter] = useState(false);

    const [allFilterData, setAllFilterData] = useState({
        page_num: 1,
        from_date: "DD-MM-YYYY",
        to_date: "DD-MM-YYYY",
        order_type: ORDER_TYPES[0],
        goal_type: GOAL_TYPE[0],
        payment_status: PAYMENT_STATUS_OPTION[0],
        order_status: ORDER_STATUS_OPTION[0],
        payment_type: PAYMENT_METHODS[0],
        query: "",
    })


    const _updateFilterData = (property, value) => {
        setAllFilterData(prevState => ({
            ...prevState,
            [property]: value
        }));
    };


    useEffect(() => {
        if (filterEnabled === true) {
            setFromDate(allFilterData.from_date);
            setEndDate(allFilterData.to_date);
            setSelectedOrderType(allFilterData.order_type);
            setSelectedMilestone(allFilterData.goal_type);
            setSelectedPaymentStatus(allFilterData.payment_status);
            setSelectedOrderStatus(allFilterData.order_status);
            setSelectedPaymentType(allFilterData.payment_type)
        }
        // eslint-disable-next-line
    }, [filterEnabled]);

    useEffect(() => {
        _getOrderList();
        // eslint-disable-next-line
    }, [pageNumber]);

    useEffect(() => {
        if (filter === true) {
            _getOrderList();
            setFilterEnabled(true);
            setScreenLoader(true);
        }
        // eslint-disable-next-line
    }, [filter]);


    function _handleSearch(search_value) {
        setSearch(search_value);
        if (search_value === "") {
            _getOrderList({ clearSearch: "" })
        }
    }

    function _handleOrderType(search_value) {
        setSelectedOrderType(search_value)
    }

    function _handleGoal(search_value) {
        setSelectedMilestone(search_value)
    }

    function _handleOrderStatus(search_value) {
        setSelectedOrderStatus(search_value)
    }

    function _handlePaymentType(search_value) {
        setSelectedPaymentType(search_value)
    }

    function _handlePaymentStatus(search_value) {
        setSelectedPaymentStatus(search_value)
    }

    function _handleTooltip(event, status, reason) {
        setShowTooltip(status);
        if (status) {
            const boundingRect = event.target.getBoundingClientRect();
            const tooltipX = boundingRect.left + (boundingRect.width / 2);
            const tooltipY = boundingRect.top + boundingRect.height + window.scrollY;
            setTooltipPosition({ x: tooltipX, y: tooltipY });
            setTooltipContent(reason)
        }
    }

    function _handleDate(date_range) {
        const formatted_start_date = _formatDate(date_range.from);
        const formatted_end_date = _formatDate(date_range.to);
        setFromDate(formatted_start_date);
        setEndDate(formatted_end_date);
    }

    function _handleToggleFilter() {
        setFilterEnabled(true)
    }

    function _handleNavigate(row) {
        navigate("/orders/detail", {
            state: {
                id: row.order_id,
                page: 1,
                selectedPaymentStatus: { label: "All", value: null },
                selectedOrderStatus: { label: "All", value: null },
                selectedOrderType: { label: "All", value: null },
                selectedMilestone: { label: "All", value: null },
                selectedPaymentType: { label: "All", value: null },
                fromDate: "DD-MM-YYYY",
                endDate: "DD-MM-YYYY",
                search: "",
            }
        })
    }

    function _handleFilter() {
        setFilter(true);
        setPageNumber(1);
        _updateFilterData("page_num", 1);
        _updateFilterData("from_date", fromDate);
        _updateFilterData("to_date", endDate);
        _updateFilterData("order_type", selectedOrderType);
        _updateFilterData("goal_type", selectedMilestone);
        _updateFilterData("payment_status", selectedPaymentStatus);
        _updateFilterData("order_status", selectedOrderStatus);
    }

    function _handleClear() {
        setFromDate("DD-MM-YYYY");
        setEndDate("DD-MM-YYYY");
        setSelectedOrderType(ORDER_TYPES[0]);
        setSelectedMilestone(GOAL_TYPE[0]);
        setSelectedPaymentStatus(PAYMENT_STATUS_OPTION[0]);
        setSelectedOrderStatus(ORDER_STATUS_OPTION[0]);
        setSelectedPaymentType(PAYMENT_METHODS[0])
    }

    const ORDER_COLUMNS = [
        {
            name: "ORDER NO.",
            cell: row => row.order_no === null ? "-" : row.order_no,
            width: "250px",
        },
        {
            name: "Invested in ",
            cell: row => row.name,
            width: "250px"
        },
        {
            name: "Type",
            width: "110px",
            cell: row => row.type
        },
        {
            name: "Payment method",
            width: "160px",
            cell: row => row.payment_method
        },
        {
            name: "Ordered date",
            cell: row => row.created,
            width: "150px"
        },
        {
            name: "Amount",
            cell: row => row.amount,
            width: "120px"
        },
        {
            name: "Order status",
            width: "150px",
            cell: row => <Badge status={row.order_status}
                pendingClass="e-bg-golden-yellow"
                color={row.order_status === "Success" ? Config.BADGE_COLORS.green :
                    row.order_status === "Processing" ? Config.BADGE_COLORS.orange :
                        row.order_status === "Partially success" ? Config.BADGE_COLORS.orange :
                            Config.BADGE_COLORS.red}
                onClick={() => _handleNavigate(row)} />
        },
        {
            name: "Payment status",
            width: "150px",
            cell: row => row.payment_status === null || row.type === "Withdrawal" || row.type === "SWP" || row.type === "Switch" ? "-" : <div className="d-flex position-relative">
                <Badge status={row.payment_status}
                    color={row.payment_status === "Success" ? Config.BADGE_COLORS.green :
                        row.payment_status === "Processing" ? Config.BADGE_COLORS.orange :
                            Config.BADGE_COLORS.red} onClick={() => _handleNavigate(row)} />


                {
                    row.payment_status === "Failed" &&
                    <div id={`tooltip${row.id}`} onMouseEnter={(event) => _handleTooltip(event, row.order_id, row.failure_reason)}
                        onMouseLeave={() => _handleTooltip(null, null)} >
                        <Icon icon="circle-information" size="16px"
                            className="margin-2px-left cursor-pointer"
                        />

                    </div>
                }
            </div>
        },

    ]

    // API - non investment data
    const _getOrderList = ({ clearSearch = null } = {}) => {
        setScreenLoader(true);
        const url = "/user/order-list";
        const request = {
            user_id: location.state.id,
            from_date: allFilterData.from_date === "DD-MM-YYYY" ? null : allFilterData.from_date,
            to_date: allFilterData.to_date === "DD-MM-YYYY" ? null : allFilterData.to_date,
            type: allFilterData.order_type === null ? null : allFilterData.order_type.value,
            payment_status: allFilterData.payment_status === null ? null : allFilterData.payment_status.value,
            order_status: allFilterData.order_status === null ? null : allFilterData.order_status.value,
            is_goal: allFilterData.goal_type === null ? null : allFilterData.goal_type.value,
            payment_method: allFilterData.payment_type === null ? null : allFilterData.payment_type.value,
            page_num: allFilterData.page_num,
            page_size: 12,
            query: clearSearch !== null ? clearSearch : allFilterData.query,
        }
        APIService(true, url, request).then((response) => {
            if (response.status_code === 200) {
                if (clearSearch !== null) {
                    _updateFilterData("query", clearSearch);
                    setSearch(clearSearch);
                }
                setOrderList(response.data);
            }
            else {
                setOrderList([]);
            }
            setScreenLoader(false);
            setFilter(false);
        })
    }



    return (
        <Fragment>
            <div className="bg-white border-radius-24px padding-32px-tb">
                <div className="row d-flex align-items-center mb-4">
                    <div className="col-lg-8 col-md-6">
                        <h4 className="e-font-16 e-line-height-20 e-montserrat-semi-bold color-gunmetal-gray mb-0 padding-32px-lr">
                            Orders
                        </h4>
                    </div>
                    <div className="col-lg-3 col-md-5 col-10 ps-md-0 ps-5">
                        <SearchInput value={search}
                            clear={() => _getOrderList({ clearSearch: "" })}
                            placeholder="Search with  order ID"
                            valueChange={_handleSearch}
                            handleSearch={(value) => _getOrderList({ clearSearch: value })} />
                    </div>
                    <div className="col-lg-1 col-md-1 col-2">
                        <Icon icon={filterEnabled ? "filter-active" : "filter-inactive"}
                            size={42}
                            className={`cursor-pointer`}
                            onClick={_handleToggleFilter} />
                    </div>
                </div>

                {filterEnabled && <div className="e-bg-light-gray padding-12px-tb padding-32px-lr margin-10px-bottom">
                    <div className="row">
                        <div className="col-lg-3 col-md-12">
                            <CustomCalendar
                                disabledDays={disabledDays}
                                label="Ordered on"
                                mode="range"
                                start={fromDate}
                                end={endDate}
                                selectDate={_handleDate} />
                        </div>
                        <div className="col-lg-2 col-md-3">
                            <label className="e-montserrat-medium e-font-14 e-line-height-18 color-dark-charcoal mb-1">
                                Order type
                            </label>
                            <CustomSelect value={selectedOrderType}
                                options={ORDER_TYPES}
                                selectChange={_handleOrderType} />
                        </div>
                        <div className="col-lg-2 col-md-3">
                            <label className="e-montserrat-medium e-font-14 e-line-height-18 color-dark-charcoal mb-1">
                                Investment type
                            </label>
                            <CustomSelect value={selectedMilestone}
                                options={GOAL_TYPE}
                                selectChange={_handleGoal} />
                        </div>
                        <div className="col-lg-2 col-md-3">
                            <label className="e-montserrat-medium e-font-14 e-line-height-18 color-dark-charcoal mb-1">
                                Payment status
                            </label>
                            <CustomSelect value={selectedPaymentStatus}
                                options={PAYMENT_STATUS_OPTION}
                                selectChange={_handlePaymentStatus} />
                        </div>
                        <div className="col-lg-2 col-md-3">
                            <label className="e-montserrat-medium e-font-14 e-line-height-18 color-dark-charcoal mb-1">
                                Order status
                            </label>
                            <CustomSelect value={selectedOrderStatus}
                                options={ORDER_STATUS_OPTION}
                                selectChange={_handleOrderStatus} />
                        </div>
                        <div className="col-lg-2 col-md-3">
                            <label className="e-montserrat-medium e-font-14 e-line-height-18 color-dark-charcoal mb-1">
                                Payment method
                            </label>
                            <CustomSelect value={selectedPaymentType}
                                options={PAYMENT_METHODS}
                                selectChange={_handlePaymentType} />
                        </div>
                    </div>
                    {/* apply filter */}
                    <div className=" margin-32px-top d-flex gap-24px">

                        <PrimaryButton label="Apply"
                            className="padding-14px-tb padding-38px-lr"
                            handleClick={_handleFilter} />


                        <SecondaryButton label="Clear"
                            className="padding-14px-tb padding-38px-lr"
                            handleClick={_handleClear} />

                    </div>
                </div>}
                {
                    screenLoader === true ?
                        <div className="h-80vh d-flex align-items-center justify-content-center">
                            <img src={require("../../../Assets/Images/Loader/page-loader.gif")}
                                alt={"loader"}
                                width={60}
                                height={60}
                            />
                        </div> :

                        orderList.length === 0 ?
                            <EmptyScreen className="my-5"
                                title="No orders found"
                                image={require("../../../Assets/Images/Empty/no-data.png")}
                                description="Lorem Ipsum has been the industry's standard dummy text ever since the 1500s" />
                            :
                            <div className="position-relative">


                                <DataTableContainer data={orderList}
                                    columns={ORDER_COLUMNS}
                                    customStyles={TABLE_STYLE}
                                    selectableRows={false}
                                    paginationComponentOptions={{ noRowsPerPage: true }}
                                    defaultSortFieldId={1}
                                    pagination={true}
                                    perPage={12}
                                    // paginationTotalRows={orderList.total_count}
                                    // paginationDefaultPage={orderList.current_page}
                                    rowClick={_handleNavigate} />


                                {/* <span className="e-montserrat-regular e-font-14 e-line-height-18 padding-24px-lr position-absolute color-jett-black bottom-10px">Showing {orderList.record} of {orderList.total_count} entries</span> */}

                            </div>
                }
                {showTooltip !== null && <span
                    className="e-order-list-tooltip position-absolute right-84px w-max-content"
                    style={{ top: tooltipPosition.y + 10 }}
                >
                    {tooltipContent}
                </span>}
            </div>
        </Fragment>
    )
}

export default OrdersList;